import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AuthContext } from '../contexts/AuthContext';
import { listPlans } from '../utils/apiServices';
import { getJSON } from '../utils/errors';
import '../stylesheets/actionPlanView.css';
import '../stylesheets/pitchResultView.css';

const FORMAT_LABELS = {
  pitch_5min: 'Pitch profesional · 5 min',
};

const PitchResultView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [planTitle, setPlanTitle] = useState('');
  const [pitchMd, setPitchMd] = useState('');
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user || !slug) return;

    const saved = getJSON(`pitch_result_${slug}`, null);
    if (!saved?.pitch_md) {
      setError('Este plan aún no tiene un pitch generado.');
      setLoading(false);
      return;
    }

    setPitchMd(saved.pitch_md);
    setMeta({
      format: saved.format || 'pitch_5min',
      audience: saved.audience || '',
    });

    listPlans()
      .then((data) => {
        const found = (data.results || []).find((p) => p.slug === slug);
        setPlanTitle(found?.title || slug);
      })
      .catch(() => setPlanTitle(slug))
      .finally(() => setLoading(false));
  }, [user, slug]);

  const handleBack = () => navigate('/plans/pitch/');
  const handlePrint = () => window.print();

  if (!user) return <Navigate to="/login/" replace />;

  if (loading) {
    return (
      <div className="action-plan-view">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Cargando pitch...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="action-plan-view">
        <div className="error-container">
          <div className="error-icon">🎤</div>
          <h2>Sin pitch generado</h2>
          <p>{error}</p>
          <button className="btn-back" onClick={handleBack}>
            Volver al editor de pitch
          </button>
        </div>
      </div>
    );
  }

  const formatLabel = FORMAT_LABELS[meta?.format] || 'Pitch profesional';

  return (
    <div className="action-plan-view">
      <div className="action-plan-header">
        <button className="btn-back" onClick={handleBack}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Volver al editor
        </button>
        <button className="btn-print no-print" onClick={handlePrint}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
            <rect x="6" y="14" width="12" height="8" />
          </svg>
          Imprimir
        </button>
      </div>

      <div className="action-plan-content">
        <div className="pitch-result-title-section">
          <div className="pitch-result-badge">{formatLabel}</div>
          <h1>{planTitle}</h1>
          {meta?.audience && (
            <div className="action-plan-meta">
              <span className="meta-item">
                <span className="meta-icon">🎯</span>
                {meta.audience}
              </span>
            </div>
          )}
        </div>

        <div className="action-plan-body">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ node, ...props }) => <h2 className="markdown-h1 pitch-md-h1" {...props} />,
              h2: ({ node, ...props }) => <h3 className="markdown-h2 pitch-md-h2" {...props} />,
              h3: ({ node, ...props }) => <h4 className="markdown-h3" {...props} />,
              ul: ({ node, ...props }) => <ul className="markdown-ul" {...props} />,
              ol: ({ node, ...props }) => <ol className="markdown-ol" {...props} />,
              li: ({ node, ...props }) => <li className="markdown-li" {...props} />,
              p: ({ node, ...props }) => <p className="markdown-p" {...props} />,
              strong: ({ node, ...props }) => <strong className="markdown-strong" {...props} />,
              em: ({ node, ...props }) => <em className="markdown-em" {...props} />,
              blockquote: ({ node, ...props }) => (
                <blockquote className="markdown-blockquote pitch-blockquote" {...props} />
              ),
              code: ({ node, inline, ...props }) =>
                inline ? (
                  <code className="markdown-code-inline" {...props} />
                ) : (
                  <code className="markdown-code-block" {...props} />
                ),
            }}
          >
            {pitchMd}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

export default PitchResultView;
